import { useState, useEffect, useMemo } from "react";
import { docsClient } from "@/lib/supabase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Activity,
  Trash2,
  AlertCircle,
  ExternalLink,
  Eye,
  RefreshCw,
  Globe,
  Filter,
  TimerOff,
  Calendar,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { formatJobSummary } from "@/utils/jobResultFormatter";
import { getJobDisplayName } from "@/utils/jobHelpers";
import {
  JobTypeFilter,
  JobTypeBadge,
  JobResultDisplay,
  ScheduledJobsDashboard,
} from "@/components/jobs";
import type { Job } from "@/utils/types";

export default function JobsPage() {
  const [activeJobs, setActiveJobs] = useState<Job[]>([]);
  const [recentJobs, setRecentJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [jobTypeFilter, setJobTypeFilter] = useState<string | null>(null);

  useEffect(() => {
    loadJobs();

    // Poll for active jobs every 2 seconds
    const interval = setInterval(loadJobs, 2000);
    return () => clearInterval(interval);
  }, []);

  const loadJobs = async () => {
    try {
      // Load active jobs (queued + running + crawling + filtering)
      const { data: active } = await docsClient
        .from("jobs")
        .select("*")
        .in("status", ["queued", "running", "crawling", "filtering"])
        .order("created_at", { ascending: false });

      setActiveJobs(active || []);

      // Load recent completed/failed/cancelled/timeout jobs
      const { data: recent } = await docsClient
        .from("jobs")
        .select("*")
        .in("status", ["completed", "failed", "cancelled", "timeout"])
        .order("completed_at", { ascending: false })
        .limit(50);

      setRecentJobs(recent || []);
    } catch (error) {
      console.error("Error loading jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter recent jobs by job type (client-side filtering to preserve polling)
  const filteredRecentJobs = useMemo(() => {
    if (!jobTypeFilter) return recentJobs;
    return recentJobs.filter((job) => job.job_type === jobTypeFilter);
  }, [recentJobs, jobTypeFilter]);

  const clearCompletedJobs = async () => {
    try {
      await docsClient
        .from("jobs")
        .delete()
        .in("status", ["completed", "failed", "cancelled", "timeout"]);

      loadJobs();
    } catch (error) {
      console.error("Error clearing jobs:", error);
    }
  };

  const retryJob = async (job: Job) => {
    try {
      setRetrying(job.id);

      // Reset the job to queued status
      await docsClient
        .from("jobs")
        .update({
          status: "queued",
          progress: 0,
          started_at: null,
          completed_at: null,
          error_message: null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", job.id);

      // Refresh jobs
      await loadJobs();
    } catch (error) {
      console.error("Error retrying job:", error);
    } finally {
      setRetrying(null);
    }
  };

  const cancelJob = async (jobId: string) => {
    if (!confirm("Are you sure you want to cancel this job?")) return;

    setCancelling(jobId);
    console.log("[JobsPage] Cancelling job:", jobId);
    try {
      const { error } = await docsClient.rpc("cancel_crawl_job", {
        p_job_id: jobId,
      });
      if (error) throw error;
      await loadJobs();
    } catch (error) {
      console.error("Failed to cancel job:", error);
    } finally {
      setCancelling(null);
    }
  };

  const getJobTypeIcon = (jobType: string) => {
    switch (jobType) {
      case "document_process":
        return <Activity className="h-4 w-4" />;
      case "quality_check":
      case "auto_fix":
        return <CheckCircle className="h-4 w-4" />;
      case "export":
        return <Activity className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "queued":
        return (
          <Badge variant="secondary">
            <Clock className="mr-1 h-3 w-3" />
            Queued
          </Badge>
        );
      case "running":
        return (
          <Badge variant="default" className="bg-blue-600">
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            Running
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="default" className="bg-green-600">
            <CheckCircle className="mr-1 h-3 w-3" />
            Completed
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Failed
          </Badge>
        );
      case "cancelled":
        return (
          <Badge variant="outline">
            <AlertCircle className="mr-1 h-3 w-3" />
            Cancelled
          </Badge>
        );
      case "crawling":
        return (
          <Badge variant="default" className="bg-blue-600">
            <Globe className="mr-1 h-3 w-3 animate-pulse" />
            Crawling
          </Badge>
        );
      case "filtering":
        return (
          <Badge variant="default" className="bg-blue-600">
            <Filter className="mr-1 h-3 w-3" />
            Filtering
          </Badge>
        );
      case "timeout":
        return (
          <Badge variant="default" className="bg-amber-600">
            <TimerOff className="mr-1 h-3 w-3" />
            Timed Out
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Format duration in a human-readable way
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Jobs</h1>
        <p className="text-muted-foreground mt-2">
          Real-time monitoring of all background tasks and processing jobs
        </p>
      </div>

      <Tabs defaultValue="active" className="space-y-6">
        <TabsList>
          <TabsTrigger value="active">
            <Activity className="mr-2 h-4 w-4" />
            Active {activeJobs.length > 0 && `(${activeJobs.length})`}
          </TabsTrigger>
          <TabsTrigger value="recent">
            <Clock className="mr-2 h-4 w-4" />
            Recent
          </TabsTrigger>
          <TabsTrigger value="scheduled">
            <Calendar className="mr-2 h-4 w-4" />
            Scheduled
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Active Jobs</CardTitle>
              <CardDescription>
                Jobs currently queued, running, crawling, or filtering
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : activeJobs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No active jobs</div>
              ) : (
                <div className="space-y-3">
                  {activeJobs.map((job) => (
                    <div key={job.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {getJobTypeIcon(job.job_type)}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{job.job_name}</p>
                              <JobTypeBadge job={job} />
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {getJobDisplayName(job)}
                            </p>
                          </div>
                        </div>
                        {getStatusBadge(job.status)}
                      </div>
                      {job.resource_url && (
                        <p className="text-sm text-muted-foreground mb-2 truncate">
                          {job.resource_url}
                        </p>
                      )}
                      {job.progress > 0 && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>Progress</span>
                            <span>{job.progress}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full transition-all duration-300"
                              style={{ width: `${job.progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-muted-foreground">
                          Started{" "}
                          {formatDistanceToNow(new Date(job.started_at || job.created_at), {
                            addSuffix: true,
                          })}
                        </p>
                        {(job.status === "crawling" || job.status === "filtering") && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => cancelJob(job.id)}
                            disabled={cancelling === job.id}
                            data-testid={`cancel-job-btn-${job.id}`}
                          >
                            {cancelling === job.id ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <XCircle className="h-4 w-4 mr-1" />
                            )}
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recent" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Recent Jobs</CardTitle>
                  <CardDescription>
                    Last 50 completed, failed, cancelled, or timed out jobs
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <JobTypeFilter value={jobTypeFilter} onChange={setJobTypeFilter} />
                  {recentJobs.length > 0 && (
                    <Button variant="outline" size="sm" onClick={clearCompletedJobs}>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Clear All
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredRecentJobs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {jobTypeFilter ? "No jobs match the selected filter" : "No recent jobs"}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Summary</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRecentJobs.map((job) => {
                      const duration =
                        job.completed_at && job.started_at
                          ? Math.round(
                              (new Date(job.completed_at).getTime() -
                                new Date(job.started_at).getTime()) /
                                1000
                            )
                          : null;

                      return (
                        <TableRow key={job.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{job.job_name}</p>
                              <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                {getJobDisplayName(job)}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <JobTypeBadge job={job} />
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(job.status)}
                            {job.error_message && (
                              <p className="text-xs text-destructive mt-1 truncate max-w-[150px]">
                                {job.error_message}
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="max-w-md truncate text-sm">
                            {formatJobSummary(job)}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {duration !== null ? formatDuration(duration) : "-"}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {job.completed_at
                              ? formatDistanceToNow(new Date(job.completed_at), { addSuffix: true })
                              : "-"}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedJob(job)}
                                data-testid={`view-job-btn-${job.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {job.status === "failed" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => retryJob(job)}
                                  disabled={retrying === job.id}
                                  data-testid={`retry-job-btn-${job.id}`}
                                >
                                  {retrying === job.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <RefreshCw className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="scheduled" className="space-y-6">
          <ScheduledJobsDashboard />
        </TabsContent>
      </Tabs>

      {/* Job Details Dialog */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedJob && getJobTypeIcon(selectedJob.job_type)}
              {selectedJob && getJobDisplayName(selectedJob)}
              {selectedJob && <JobTypeBadge job={selectedJob} />}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2">
              {selectedJob && getStatusBadge(selectedJob.status)}
            </DialogDescription>
          </DialogHeader>

          {selectedJob && (
            <div className="space-y-6">
              {/* Resource */}
              {selectedJob.resource_url && (
                <div>
                  <h3 className="text-sm font-medium mb-2">Resource</h3>
                  <a
                    href={selectedJob.resource_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    {selectedJob.resource_url}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}

              {/* Timing Section */}
              <div>
                <h3 className="text-sm font-medium mb-3">Timing</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="bg-muted rounded-lg p-3">
                    <div className="text-muted-foreground text-xs mb-1">Started</div>
                    <div className="font-medium">
                      {selectedJob.started_at
                        ? format(new Date(selectedJob.started_at), "MMM d, yyyy 'at' h:mm a")
                        : "Not started"}
                    </div>
                  </div>
                  <div className="bg-muted rounded-lg p-3">
                    <div className="text-muted-foreground text-xs mb-1">Completed</div>
                    <div className="font-medium">
                      {selectedJob.completed_at
                        ? format(new Date(selectedJob.completed_at), "MMM d, yyyy 'at' h:mm a")
                        : "Not completed"}
                    </div>
                  </div>
                  <div className="bg-muted rounded-lg p-3">
                    <div className="text-muted-foreground text-xs mb-1">Duration</div>
                    <div className="font-medium">
                      {selectedJob.started_at && selectedJob.completed_at
                        ? formatDuration(
                            Math.round(
                              (new Date(selectedJob.completed_at).getTime() -
                                new Date(selectedJob.started_at).getTime()) /
                                1000
                            )
                          )
                        : "-"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress */}
              {selectedJob.progress > 0 && selectedJob.progress < 100 && (
                <div>
                  <h3 className="text-sm font-medium mb-2">Progress</h3>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Progress</span>
                      <span>{selectedJob.progress}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${selectedJob.progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Result - Using JobResultDisplay component */}
              <div>
                <h3 className="text-sm font-medium mb-3">Result</h3>
                <JobResultDisplay job={selectedJob} />
              </div>

              {/* Error */}
              {selectedJob.error_message && (
                <div>
                  <h3 className="text-sm font-medium mb-2 text-destructive">Error</h3>
                  <p className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                    {selectedJob.error_message}
                  </p>
                </div>
              )}

              {/* Metadata */}
              <div className="pt-4 border-t">
                <h3 className="text-sm font-medium mb-2">Metadata</h3>
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <div>
                    <span className="font-medium">ID:</span> {selectedJob.id}
                  </div>
                  <div>
                    <span className="font-medium">Type:</span> {selectedJob.job_type}
                  </div>
                  <div>
                    <span className="font-medium">Resource Type:</span>{" "}
                    {selectedJob.resource_type || "-"}
                  </div>
                  <div>
                    <span className="font-medium">Resource ID:</span>{" "}
                    {selectedJob.resource_id || "-"}
                  </div>
                </div>
              </div>

              {/* Retry Button */}
              {selectedJob.status === "failed" && (
                <div className="pt-4 border-t">
                  <Button
                    onClick={() => {
                      retryJob(selectedJob);
                      setSelectedJob(null);
                    }}
                    disabled={retrying === selectedJob.id}
                    className="w-full"
                    data-testid="dialog-retry-job-btn"
                  >
                    {retrying === selectedJob.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Retrying...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Retry Job
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
